import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import PageLayout from "components/layouts/PageLayout";
import ConversationFeedback from "lib/api/ConversationFeedback";
import Company from "lib/api/Company";
import { Button, ButtonGroup } from "components/common";

export default function ConversationFeedbackDetail(props) {
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(true);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [messageFilters, setMessageFilters] = useState({
    assistant: true,
    user: true,
    function_call: true,
    data_channel: true
  });

  async function load(uuid) {
    try {
      setLoading(true);
      const response = await ConversationFeedback.load(uuid);
      if (response.status === 200) {
        setFeedback(response.data);

        // Load company information if company_uuid exists
        if (response.data.company_uuid) {
          try {
            const companyResponse = await Company.load(response.data.company_uuid);
            if (companyResponse) {
              setCompanyInfo({
                name: companyResponse.name,
                client_name: companyResponse.client?.name || 'Unknown Client'
              });
            }
          } catch (error) {
            console.error('Failed to fetch company data:', error);
          }
        }
      } else if (response.status === 401) {
        props.history.push(`/`);
      } else {
        console.log('An unhandled api error occurred.');
        console.log(response);
      }
    } catch (exception) {
      console.log(exception);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (props.match.params.uuid) {
      load(props.match.params.uuid);
    }
  }, [props.match.params.uuid]);

  const formatFeedbackType = (type) => {
    if (!type) return "";
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const getFeedbackTypeColor = (type) => {
    switch (type) {
      case 'positive':
        return '#10b981'; // green
      case 'negative':
        return '#ef4444'; // red
      case 'neutral':
        return '#6b7280'; // gray
      default:
        return '#000000';
    }
  };

  const formatFeedbackReasons = (reasons) => {
    if (!reasons || reasons.length === 0) return "None";
    return reasons.map(reason => {
      return reason.split('_').map(word =>
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
    }).join(', ');
  };

  const formatRole = (role) => {
    return role.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const getMessageStyle = (role) => {
    const styles = {
      assistant: {
        backgroundColor: '#f0f9ff',
        borderColor: '#3b82f6',
        textColor: '#3b82f6'
      },
      user: {
        backgroundColor: '#f5f5f5',
        borderColor: '#6b7280',
        textColor: '#6b7280'
      },
      function_call: {
        backgroundColor: '#fef3c7',
        borderColor: '#f59e0b',
        textColor: '#f59e0b'
      },
      data_channel: {
        backgroundColor: '#e0e7ff',
        borderColor: '#6366f1',
        textColor: '#6366f1'
      }
    };
    return styles[role] || styles.user;
  };

  const renderMessageContent = (message) => {
    if (message.role === 'function_call') {
      return (
        <div>
          <div style={{ marginBottom: '8px' }}>
            <strong>Function:</strong> {message.function_name}
          </div>
          {message.arguments && (
            <div>
              <strong>Arguments:</strong>
              <pre style={{
                backgroundColor: '#ffffff',
                padding: '10px',
                borderRadius: '4px',
                marginTop: '5px',
                overflowX: 'auto',
                fontSize: '0.9em'
              }}>
                {JSON.stringify(message.arguments, null, 2)}
              </pre>
            </div>
          )}
        </div>
      );
    }

    if (message.role === 'data_channel') {
      return (
        <div>
          {message.message}
        </div>
      );
    }

    return message.content || message.message || 'No content';
  };

  if (loading) {
    return (
      <PageLayout>
        <h1>Conversation Log Detail</h1>
        <div className="hr hr--darkest"></div>
        <p>Loading...</p>
      </PageLayout>
    );
  }

  if (!feedback) {
    return (
      <PageLayout>
        <h1>Conversation Log Detail</h1>
        <div className="hr hr--darkest"></div>
        <p>Feedback not found.</p>
        <ButtonGroup>
          <Link className="button" to="/conversation-feedback">
            Back to List
          </Link>
        </ButtonGroup>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <h1>Conversation Log Detail</h1>
      <div className="hr hr--darkest"></div>

      <div className="row">
        <div className="col-xs-12">
          <ButtonGroup>
            <Link className="button" to="/conversation-feedback">
              Back to List
            </Link>
          </ButtonGroup>
        </div>
      </div>

      <div className="row" style={{ marginTop: '20px' }}>
        <div className="col-xs-12">
          <h2>Feedback Information</h2>
          <div className="hr"></div>
        </div>
      </div>

      <div className="row">
        <div className="col-xs-12 col-sm-3">
          <div style={{ marginBottom: '15px' }}>
            <strong>Client Name:</strong>
            <div>{companyInfo?.client_name || 'Loading...'}</div>
          </div>
        </div>
        <div className="col-xs-12 col-sm-3">
          <div style={{ marginBottom: '15px' }}>
            <strong>Company Name:</strong>
            <div>{companyInfo?.name || 'Loading...'}</div>
          </div>
        </div>
        <div className="col-xs-12 col-sm-3">
          <div style={{ marginBottom: '15px' }}>
            <strong>Conversation Type:</strong>
            <div>{formatFeedbackType(feedback.conversation_type)}</div>
          </div>
        </div>
        <div className="col-xs-12 col-sm-3">
          <div style={{ marginBottom: '15px' }}>
            <strong>Feedback Type:</strong>
            <div style={{ color: getFeedbackTypeColor(feedback.feedback_type) }}>
              {formatFeedbackType(feedback.feedback_type)}
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-xs-12 col-sm-3">
          <div style={{ marginBottom: '15px' }}>
            <strong>Conversation ID:</strong>
            <div>{feedback.conversation_id}</div>
          </div>
        </div>
        <div className="col-xs-12 col-sm-3">
          <div style={{ marginBottom: '15px' }}>
            <strong>Created At:</strong>
            <div>{new Date(feedback.created_at).toLocaleString()}</div>
          </div>
        </div>
        <div className="col-xs-12 col-sm-6">
          <div style={{ marginBottom: '15px' }}>
            <strong>Feedback Reasons:</strong>
            <div>{formatFeedbackReasons(feedback.feedback_reasons)}</div>
          </div>
        </div>
      </div>

      {feedback.comment && (
        <>
          <div className="row" style={{ marginTop: '30px' }}>
            <div className="col-xs-12">
              <h2>Comment</h2>
              <div className="hr"></div>
            </div>
          </div>

          <div className="row">
            <div className="col-xs-12">
              <div style={{ padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                {feedback.comment}
              </div>
            </div>
          </div>
        </>
      )}

      {feedback.conversation_data && (
        <>
          <div className="row" style={{ marginTop: '30px' }}>
            <div className="col-xs-12">
              <h2>Conversation Details</h2>
              <div className="hr"></div>
            </div>
          </div>

          <div className="row">
            <div className="col-xs-12 col-sm-3">
              <div style={{ marginBottom: '15px' }}>
                <strong>Platform:</strong>
                <div>{feedback.conversation_data.platform || 'N/A'}</div>
              </div>
            </div>
            <div className="col-xs-12 col-sm-3">
              <div style={{ marginBottom: '15px' }}>
                <strong>App Build:</strong>
                <div>{feedback.conversation_data.app_build || 'N/A'}</div>
              </div>
            </div>
            <div className="col-xs-12 col-sm-3">
              <div style={{ marginBottom: '15px' }}>
                <strong>Device Details:</strong>
                <div>{feedback.conversation_data.device_details || 'N/A'}</div>
              </div>
            </div>
            <div className="col-xs-12 col-sm-3">
              <div style={{ marginBottom: '15px' }}>
                <strong>Session Duration:</strong>
                <div>{feedback.conversation_data.session_duration_seconds ? `${feedback.conversation_data.session_duration_seconds}s` : 'N/A'}</div>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-xs-12 col-sm-3">
              <div style={{ marginBottom: '15px' }}>
                <strong>User Spoke:</strong>
                <div>{feedback.conversation_data.user_spoke ? 'Yes' : 'No'}</div>
              </div>
            </div>
            <div className="col-xs-12 col-sm-3">
              <div style={{ marginBottom: '15px' }}>
                <strong>Record Created:</strong>
                <div>{feedback.conversation_data.successfully_created_record !== undefined ? (feedback.conversation_data.successfully_created_record ? 'Yes' : 'No') : 'N/A'}</div>
              </div>
            </div>
            <div className="col-xs-12 col-sm-3">
              <div style={{ marginBottom: '15px' }}>
                <strong>Daily Usage Count:</strong>
                <div>{feedback.conversation_data.daily_usage_count || 0}</div>
              </div>
            </div>
            <div className="col-xs-12 col-sm-3">
              <div style={{ marginBottom: '15px' }}>
                <strong>Lifetime Usage Count:</strong>
                <div>{feedback.conversation_data.lifetime_usage_count || 0}</div>
              </div>
            </div>
          </div>

          {feedback.conversation_data.token_usage && Object.keys(feedback.conversation_data.token_usage).length > 0 && (
            <>
              <div className="row" style={{ marginTop: '30px' }}>
                <div className="col-xs-12">
                  <h2>Token Usage</h2>
                  <div className="hr"></div>
                </div>
              </div>

              <div className="row">
                <div className="col-xs-12 col-sm-3">
                  <div style={{ marginBottom: '15px' }}>
                    <strong>Total Tokens:</strong>
                    <div>{feedback.conversation_data.token_usage.totalTokens?.toLocaleString() || 0}</div>
                  </div>
                </div>
                <div className="col-xs-12 col-sm-3">
                  <div style={{ marginBottom: '15px' }}>
                    <strong>Input Tokens:</strong>
                    <div>{feedback.conversation_data.token_usage.inputTokens?.toLocaleString() || 0}</div>
                  </div>
                </div>
                <div className="col-xs-12 col-sm-3">
                  <div style={{ marginBottom: '15px' }}>
                    <strong>Output Tokens:</strong>
                    <div>{feedback.conversation_data.token_usage.outputTokens?.toLocaleString() || 0}</div>
                  </div>
                </div>
                <div className="col-xs-12 col-sm-3">
                  <div style={{ marginBottom: '15px' }}>
                    <strong>Cached Text Tokens:</strong>
                    <div>{feedback.conversation_data.token_usage.cachedTextTokens?.toLocaleString() || 0}</div>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-xs-12 col-sm-3">
                  <div style={{ marginBottom: '15px' }}>
                    <strong>Audio Input Tokens:</strong>
                    <div>{feedback.conversation_data.token_usage.audioInputTokens?.toLocaleString() || 0}</div>
                  </div>
                </div>
                <div className="col-xs-12 col-sm-3">
                  <div style={{ marginBottom: '15px' }}>
                    <strong>Audio Output Tokens:</strong>
                    <div>{feedback.conversation_data.token_usage.audioOutputTokens?.toLocaleString() || 0}</div>
                  </div>
                </div>
                <div className="col-xs-12 col-sm-3">
                  <div style={{ marginBottom: '15px' }}>
                    <strong>Cached Audio Tokens:</strong>
                    <div>{feedback.conversation_data.token_usage.cachedAudioTokens?.toLocaleString() || 0}</div>
                  </div>
                </div>
                <div className="col-xs-12 col-sm-3">
                  <div style={{ marginBottom: '15px' }}>
                    <strong>Session Duration:</strong>
                    <div>{feedback.conversation_data.token_usage.sessionDurationSeconds ? `${feedback.conversation_data.token_usage.sessionDurationSeconds}s` : 'N/A'}</div>
                  </div>
                </div>
              </div>
            </>
          )}

          {feedback.conversation_data.conversation_messages && feedback.conversation_data.conversation_messages.length > 0 && (
            <>
              <div className="row" style={{ marginTop: '30px' }}>
                <div className="col-xs-12">
                  <h2>Conversation Messages</h2>
                  <div className="hr"></div>
                </div>
              </div>

              <div className="row" style={{ marginBottom: '20px' }}>
                <div className="col-xs-12">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <strong>Filters:</strong>
                    <div
                      onClick={() => setMessageFilters({ ...messageFilters, assistant: !messageFilters.assistant })}
                      style={{
                        padding: '8px 15px',
                        backgroundColor: messageFilters.assistant ? '#f0f9ff' : '#e5e7eb',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb',
                        borderLeft: `4px solid ${messageFilters.assistant ? '#3b82f6' : '#9ca3af'}`,
                        cursor: 'pointer',
                        opacity: messageFilters.assistant ? 1 : 0.5,
                        transition: 'all 0.2s'
                      }}
                    >
                      <span style={{ color: messageFilters.assistant ? '#3b82f6' : '#6b7280', fontWeight: 'bold' }}>
                        Assistant
                      </span>
                    </div>
                    <div
                      onClick={() => setMessageFilters({ ...messageFilters, user: !messageFilters.user })}
                      style={{
                        padding: '8px 15px',
                        backgroundColor: messageFilters.user ? '#f5f5f5' : '#e5e7eb',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb',
                        borderLeft: `4px solid ${messageFilters.user ? '#6b7280' : '#9ca3af'}`,
                        cursor: 'pointer',
                        opacity: messageFilters.user ? 1 : 0.5,
                        transition: 'all 0.2s'
                      }}
                    >
                      <span style={{ color: '#6b7280', fontWeight: 'bold' }}>
                        User
                      </span>
                    </div>
                    <div
                      onClick={() => setMessageFilters({ ...messageFilters, function_call: !messageFilters.function_call })}
                      style={{
                        padding: '8px 15px',
                        backgroundColor: messageFilters.function_call ? '#fef3c7' : '#e5e7eb',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb',
                        borderLeft: `4px solid ${messageFilters.function_call ? '#f59e0b' : '#9ca3af'}`,
                        cursor: 'pointer',
                        opacity: messageFilters.function_call ? 1 : 0.5,
                        transition: 'all 0.2s'
                      }}
                    >
                      <span style={{ color: messageFilters.function_call ? '#f59e0b' : '#6b7280', fontWeight: 'bold' }}>
                        Function Call
                      </span>
                    </div>
                    <div
                      onClick={() => setMessageFilters({ ...messageFilters, data_channel: !messageFilters.data_channel })}
                      style={{
                        padding: '8px 15px',
                        backgroundColor: messageFilters.data_channel ? '#e0e7ff' : '#e5e7eb',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb',
                        borderLeft: `4px solid ${messageFilters.data_channel ? '#6366f1' : '#9ca3af'}`,
                        cursor: 'pointer',
                        opacity: messageFilters.data_channel ? 1 : 0.5,
                        transition: 'all 0.2s'
                      }}
                    >
                      <span style={{ color: messageFilters.data_channel ? '#6366f1' : '#6b7280', fontWeight: 'bold' }}>
                        Data Channel
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-xs-12">
                  {feedback.conversation_data.conversation_messages
                    .filter(message => messageFilters[message.role])
                    .map((message, index) => {
                    const style = getMessageStyle(message.role);
                    return (
                      <div
                        key={index}
                        style={{
                          marginBottom: '15px',
                          padding: '15px',
                          backgroundColor: style.backgroundColor,
                          borderRadius: '8px',
                          border: '1px solid #e5e7eb',
                          borderLeft: `4px solid ${style.borderColor}`
                        }}
                      >
                        <div style={{ marginBottom: '8px' }}>
                          <strong style={{ color: style.textColor }}>
                            {formatRole(message.role)}
                          </strong>
                          <span style={{ marginLeft: '10px', fontSize: '0.9em', color: '#6b7280' }}>
                            {new Date(message.timestamp).toLocaleString()}
                          </span>
                        </div>
                        <div style={{ lineHeight: '1.6' }}>
                          {renderMessageContent(message)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </>
      )}
    </PageLayout>
  );
}
